// The in-chat Share Card pair — pure helpers shared by the chat state (use-chat), the BFF history
// mapping (messages API) and the card UI (MessageRow/ShareOfferCard, MessageRow/ShareResponseRow).
//
// Two markers, two very different rows:
//
//   `shareOfferCard`     rides an ASSISTANT reply — the card as the recipient will meet it, plus the
//                        name chips that SEND on a tap (design §7.1).
//   `shareResponseCard`  IS its own row — for a bare emoji the design gives the response a templated
//                        row and NO llm turn (§8), so nothing composes this one at read time.
//
// Every string on both arrives final and already localized by the server (the captions too), so this
// surface needs no i18n keys for them — the same rule `lib/outreach-preview-card.ts` follows and for
// the same reason. Deliberately dependency-free: the validation here MIRRORS common-lib's
// `readShareOfferCard` / `readShareResponseCard` rather than importing it, because a `lib/` module that
// reaches common-lib fails to PARSE in CI (see the jest note in this package's CLAUDE.md). Keep the
// two in step.

export const SHARE_CARD_KINDS = ['FILE', 'CHOICE', 'TASK'] as const;
export type ShareCardKindTag = (typeof SHARE_CARD_KINDS)[number];

export const SHARE_CARD_CHANNELS = ['whatsapp', 'email'] as const;
export type ShareCardChannel = (typeof SHARE_CARD_CHANNELS)[number];

export const SHARE_CARD_CHIP_STATES = ['ready', 'sending', 'sent', 'undone', 'undo_refused', 'failed'] as const;
export type ShareCardChipState = (typeof SHARE_CARD_CHIP_STATES)[number];

export const SHARE_CARD_RESPONSE_KINDS = ['reaction', 'option', 'chip', 'reply'] as const;
export type ShareCardResponseKind = (typeof SHARE_CARD_RESPONSE_KINDS)[number];

/** The window the client counts down when the server names none. The SERVICE is still the authority. */
export const SHARE_CARD_UNDO_SECONDS = 10;

/**
 * The share offer's chips are the reply's ORDINARY choice chips, and these four functions are the whole
 * coupling between a tap and what it authorizes.
 *
 * A metadata key a shipped app has never heard of reads as absent, so a card whose send lived inside
 * the new component could not be sent at all on a build already in the field. Choice chips have been
 * understood by every build since 2026-07-25, so an old app renders the same names, a tap still
 * resolves to the same send, and the card is purely the picture it could not draw.
 *
 * The person id rides the option ID, never the label: a label is display copy — translated, rewritten
 * by designers, and on an old build the only thing the user sees — so resolving a tap by parsing it
 * would make the send depend on the wording of a button. MIRRORS common-lib's helpers of the same
 * names; keep the two in step.
 *
 * ⚠️ The send label must SAY that a tap sends. `labels.chipsHint` says it on a client that can draw
 * this card — which is precisely the client an app predating the key is not — so on every build in the
 * field the label is the only cue left. See the fuller note on common-lib's copy of these helpers.
 */
export const SHARE_OFFER_ASK_PREFIX = 'share-offer:';
export const SHARE_SEND_OPTION_PREFIX = 'share-send:';
export const SHARE_UNDO_OPTION_PREFIX = 'share-undo:';

const idAfter = (value: string | undefined | null, prefix: string): string | null =>
  typeof value === 'string' && value.startsWith(prefix) && value.slice(prefix.length).trim().length > 0 ? value.slice(prefix.length).trim() : null;

export const shareOfferAskId = (offerId: string): string => `${SHARE_OFFER_ASK_PREFIX}${offerId}`;
export const offerIdFromShareOfferAskId = (askId: string | undefined | null): string | null => idAfter(askId, SHARE_OFFER_ASK_PREFIX);
export const shareSendOptionId = (personId: string): string => `${SHARE_SEND_OPTION_PREFIX}${personId}`;
/** A foreign option id answers `null` rather than a bogus person: a tap it cannot read is not a send. */
export const personIdFromShareSendOptionId = (optionId: string | undefined | null): string | null => idAfter(optionId, SHARE_SEND_OPTION_PREFIX);
export const shareUndoOptionId = (cardId: string): string => `${SHARE_UNDO_OPTION_PREFIX}${cardId}`;
export const cardIdFromShareUndoOptionId = (optionId: string | undefined | null): string | null => idAfter(optionId, SHARE_UNDO_OPTION_PREFIX);

export type ShareOfferChipData = {
  personId: string;
  displayName: string;
  channel: ShareCardChannel;
  /** The chip's subtitle, already in the user's language ("via WhatsApp"). */
  channelLabel: string;
  state: ShareCardChipState;
  /** The id the send returned. Undo needs it, so a chip without one can never offer Undo. */
  cardId?: string;
  /** ISO instant the hold began — the countdown runs from here, not from render. */
  sentAt?: string;
};

export type ShareOfferOptionData = { id: string; label: string; mediaUrl?: string };
export type ShareOfferMediaData = { url: string; mimeType?: string; fileName?: string; posterUrl?: string };

export type ShareOfferCardLabels = {
  sendingTo: string;
  undo: string;
  sentTo: string;
  undone: string;
  undoTooLate: string;
  failed: string;
  options: string;
  chipsHint: string;
  previewOnly: string;
};

export type ShareOfferCardData = {
  offerId: string;
  kind: ShareCardKindTag;
  title: string;
  note?: string;
  body?: string;
  media?: ShareOfferMediaData;
  options?: ShareOfferOptionData[];
  chips: ShareOfferChipData[];
  previewOnly?: boolean;
  undoSeconds: number;
  labels: ShareOfferCardLabels;
};

export type ShareResponseRowData = {
  cardId: string;
  personName: string;
  kind: ShareCardResponseKind;
  reaction?: string;
  answer?: string;
  cardTitle?: string;
  /** The whole sentence, written by the server. */
  line: string;
};

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const trimmed = (value: unknown): string | undefined => (nonEmpty(value) ? value.trim() : undefined);

function readChip(raw: unknown): ShareOfferChipData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const chip = raw as Record<string, unknown>;
  if (!nonEmpty(chip.personId) || !nonEmpty(chip.displayName)) return undefined;
  if (!(SHARE_CARD_CHANNELS as readonly string[]).includes(chip.channel as string)) return undefined;
  const channel = chip.channel as ShareCardChannel;
  const cardId = trimmed(chip.cardId);
  // An unknown state falls back to `ready` — EXCEPT that a chip already spent must never fall back to
  // a tappable one, which is why a `cardId` alone promotes it to `sent`. Re-offering a tap on a card
  // that has gone out is the one wrong answer available here.
  const known = (SHARE_CARD_CHIP_STATES as readonly string[]).includes(chip.state as string) ? (chip.state as ShareCardChipState) : undefined;
  const sentAt = trimmed(chip.sentAt);
  return {
    personId: chip.personId.trim(),
    displayName: chip.displayName.trim(),
    channel,
    channelLabel: trimmed(chip.channelLabel) ?? (channel === 'whatsapp' ? 'via WhatsApp' : 'via email'),
    state: known ?? (cardId ? 'sent' : 'ready'),
    ...(cardId ? { cardId } : {}),
    ...(sentAt ? { sentAt } : {}),
  };
}

function readOptions(raw: unknown): ShareOfferOptionData[] {
  if (!Array.isArray(raw)) return [];
  const options: ShareOfferOptionData[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const option = item as Record<string, unknown>;
    if (!nonEmpty(option.id) || !nonEmpty(option.label)) continue;
    const mediaUrl = trimmed(option.mediaUrl);
    options.push({ id: option.id.trim(), label: option.label.trim(), ...(mediaUrl ? { mediaUrl } : {}) });
  }
  return options;
}

/**
 * Read + sanitize `metadata.shareOfferCard` off a raw message entity.
 *
 * No `offerId` renders nothing: without it a chip authorizes nothing, so there is no card to draw.
 * Unreadable chips are dropped one at a time, and a card left with none becomes a PREVIEW rather than
 * a card whose names silently disappeared — which is also what an older harness's `previewOnly` card
 * looks like on the wire, and the two must render the same because they are the same card.
 */
export function readShareOfferCard(metadata: unknown): ShareOfferCardData | undefined {
  const raw = (metadata as { shareOfferCard?: unknown } | undefined)?.shareOfferCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const card = raw as Record<string, unknown>;
  if (!nonEmpty(card.offerId) || !nonEmpty(card.title)) return undefined;
  if (!(SHARE_CARD_KINDS as readonly string[]).includes(card.kind as string)) return undefined;

  const chips = (Array.isArray(card.chips) ? card.chips : []).map(readChip).filter((chip): chip is ShareOfferChipData => chip !== undefined);
  const options = readOptions(card.options);
  const rawMedia = (card.media && typeof card.media === 'object' ? card.media : {}) as Record<string, unknown>;
  const mediaUrl = trimmed(rawMedia.url);
  const labels = (card.labels && typeof card.labels === 'object' ? card.labels : {}) as Record<string, unknown>;
  const undoSeconds = typeof card.undoSeconds === 'number' && card.undoSeconds > 0 ? Math.floor(card.undoSeconds) : SHARE_CARD_UNDO_SECONDS;

  return {
    offerId: card.offerId.trim(),
    kind: card.kind as ShareCardKindTag,
    title: card.title.trim(),
    ...(trimmed(card.note) ? { note: trimmed(card.note)! } : {}),
    ...(trimmed(card.body) ? { body: trimmed(card.body)! } : {}),
    ...(mediaUrl
      ? {
          media: {
            url: mediaUrl,
            ...(trimmed(rawMedia.mimeType) ? { mimeType: trimmed(rawMedia.mimeType)! } : {}),
            ...(trimmed(rawMedia.fileName) ? { fileName: trimmed(rawMedia.fileName)! } : {}),
            ...(trimmed(rawMedia.posterUrl) ? { posterUrl: trimmed(rawMedia.posterUrl)! } : {}),
          },
        }
      : {}),
    ...(options.length > 0 ? { options } : {}),
    chips,
    ...(card.previewOnly === true || chips.length === 0 ? { previewOnly: true } : {}),
    undoSeconds,
    labels: {
      sendingTo: trimmed(labels.sendingTo) ?? 'Sending to {name}…',
      undo: trimmed(labels.undo) ?? 'Undo',
      sentTo: trimmed(labels.sentTo) ?? 'Sent to {name}',
      undone: trimmed(labels.undone) ?? 'Taken back — it never went out',
      undoTooLate: trimmed(labels.undoTooLate) ?? 'Too late to take that one back; it had already gone',
      failed: trimmed(labels.failed) ?? 'It did not go out',
      options: trimmed(labels.options) ?? 'They pick one',
      chipsHint: trimmed(labels.chipsHint) ?? 'Tap a name to send it',
      previewOnly: trimmed(labels.previewOnly) ?? 'Have a look before this goes anywhere',
    },
  };
}

/**
 * Read + sanitize `metadata.shareResponseCard`.
 *
 * `line` IS the row — a finished sentence the server wrote — so a marker without one renders nothing
 * rather than being stitched together here out of the parts: a client assembling its own sentence
 * would word the same reaction differently from the push notification about it.
 */
export function readShareResponseCard(metadata: unknown): ShareResponseRowData | undefined {
  const raw = (metadata as { shareResponseCard?: unknown } | undefined)?.shareResponseCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const card = raw as Record<string, unknown>;
  if (!nonEmpty(card.cardId) || !nonEmpty(card.personName) || !nonEmpty(card.line)) return undefined;
  const kind = (SHARE_CARD_RESPONSE_KINDS as readonly string[]).includes(card.kind as string) ? (card.kind as ShareCardResponseKind) : 'reaction';
  return {
    cardId: card.cardId.trim(),
    personName: card.personName.trim(),
    kind,
    ...(trimmed(card.reaction) ? { reaction: trimmed(card.reaction)! } : {}),
    ...(trimmed(card.answer) ? { answer: trimmed(card.answer)! } : {}),
    ...(trimmed(card.cardTitle) ? { cardTitle: trimmed(card.cardTitle)! } : {}),
    line: card.line.trim(),
  };
}

/** `{name}` filled with the person the chip names. The only substitution these labels carry. */
export function fillName(template: string, name: string): string {
  return template.replace(/\{name\}/g, name);
}

/**
 * Seconds still on the Undo clock for a chip in the `sending` state, from the server's own `sentAt`
 * when it sent one.
 *
 * Anchored on `sentAt` rather than on the moment the row rendered, because a reload mid-window must
 * not restart the ten seconds — the hold is the SERVICE's, and a client clock that grants itself a
 * fresh window would offer an Undo the service has already refused. Never negative, never above the
 * declared window.
 */
export function undoSecondsLeft(chip: ShareOfferChipData, undoSeconds: number, nowMs: number): number {
  if (!chip.sentAt) return undoSeconds;
  const started = Date.parse(chip.sentAt);
  if (Number.isNaN(started)) return undoSeconds;
  const elapsed = Math.floor((nowMs - started) / 1000);
  return Math.max(0, Math.min(undoSeconds, undoSeconds - elapsed));
}

/**
 * Undo is offered only while a send is genuinely held, and only when there is a card id to take back.
 *
 * Read by whoever MINTS the undo choice (the harness), not by the card — the card renders no controls.
 */
export function canUndo(chip: ShareOfferChipData): boolean {
  return chip.state === 'sending' && nonEmpty(chip.cardId);
}
